import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes } from '../../util/sdkLoader';
import { isEmpty, pickBy } from '../../util/common';
import { fetchTrackingStatus } from '../../util/api';
import { storableError } from '../../util/errors';

const { UUID } = sdkTypes;

const fetchTransactionPayloadCreator = ({ id, config }, { rejectWithValue, extra: sdk }) => {
  const imgConfig = config?.layout?.listingImage;
  const imageVariants =
    imgConfig && imgConfig.variantPrefix
      ? [
          `variants.${imgConfig.variantPrefix}`,
          `variants.${imgConfig.variantPrefix}-2x`,
        ]
      : [];
  const include = ['listing', 'listing.images', 'listing.author', 'customer', 'provider'];
  return sdk.transactions
    .show(
      { id, include },
      { expand: true, ...(imageVariants.length > 0 ? { 'fields.image': imageVariants } : {}) }
    )
    .then(res => {
      return res;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchTransactionThunk = createAsyncThunk(
  'TrackingPage/fetchTransaction',
  fetchTransactionPayloadCreator
);

const fetchTrackingStatusPayloadCreator = async ({ carrier, trackingNumber }, { rejectWithValue }) => {
  try {
    const response = await fetchTrackingStatus({ carrier, trackingNumber });
    return response.data;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchTrackingStatusThunk = createAsyncThunk(
  'TrackingPage/fetchTrackingStatus',
  fetchTrackingStatusPayloadCreator
);

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transaction: null,
  fetchTrackingStatusInProgress: false,
  fetchTrackingStatusError: null,
  trackingData: null,
};

const trackingPageSlice = createSlice({
  name: 'TrackingPage',
  initialState,
  reducers: {
    setInitialValues(state, action) {
      return { ...initialState, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTransactionThunk.pending, state => {
        state.fetchTransactionInProgress = true;
        state.fetchTransactionError = null;
      })
      .addCase(fetchTransactionThunk.fulfilled, (state, action) => {
        state.fetchTransactionInProgress = false;
        const apiResponse = action.payload;
        state.transaction = apiResponse.data.data;
        state.transactionRef = {
          id: apiResponse.data.data.id,
          type: apiResponse.data.data.type,
        };
      })
      .addCase(fetchTransactionThunk.rejected, (state, action) => {
        state.fetchTransactionInProgress = false;
        state.fetchTransactionError = action.payload;
      })
      .addCase(fetchTrackingStatusThunk.pending, state => {
        state.fetchTrackingStatusInProgress = true;
        state.fetchTrackingStatusError = null;
      })
      .addCase(fetchTrackingStatusThunk.fulfilled, (state, action) => {
        state.fetchTrackingStatusInProgress = false;
        state.trackingData = action.payload;
      })
      .addCase(fetchTrackingStatusThunk.rejected, (state, action) => {
        state.fetchTrackingStatusInProgress = false;
        state.fetchTrackingStatusError = action.payload;
      });
  },
});

export const { setInitialValues } = trackingPageSlice.actions;
export default trackingPageSlice.reducer;

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TrackingPage;
  const txRef = state.transactionRef;
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  return Promise.all([dispatch(fetchTransactionThunk({ id: txId, config }))]);
};
